import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  CircleX,
  Loader2,
} from "lucide-react";
import { useState } from "react";

import {
  FLOW_RUN_STATUS_LABELS,
  getFlowActionResultTarget,
  type FlowActionResult,
  type FlowRun,
  type FlowRunStatus,
} from "@/modules/flow";
import { mineApi, mineQueryKeys } from "@/modules/mine";
import { useSheet } from "@/shared/components/sheet";
import { formatFullDateTime, formatRelativeTime } from "@/shared/lib/date";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/shared/ui/empty";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";

import { FlowRunDetailSheet, FlowRunPath } from "./flow-run-detail-sheet";

const STATUS_STYLES: Record<FlowRunStatus, string> = {
  succeeded: "text-emerald-600 dark:text-emerald-400",
  succeeded_with_skips: "text-emerald-600 dark:text-emerald-400",
  partially_failed: "text-amber-600 dark:text-amber-400",
  failed: "text-destructive",
};

function StatusIcon({ status }: { status: FlowRunStatus }) {
  const Icon = status === "failed"
    ? CircleX
    : status === "partially_failed"
      ? AlertCircle
      : CheckCircle2;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label={FLOW_RUN_STATUS_LABELS[status]} className={STATUS_STYLES[status]} role="img">
          <Icon className="size-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{FLOW_RUN_STATUS_LABELS[status]}</TooltipContent>
    </Tooltip>
  );
}

function ItemTitle({ itemId }: { itemId: string }) {
  const query = useQuery({
    queryKey: mineQueryKeys.itemDetail(itemId),
    queryFn: () => mineApi.getItem(itemId, { silent: true }),
    retry: false,
  });
  if (query.isPending) return <>加载中…</>;
  return <>{query.data?.title || "未命名收藏"}</>;
}

function ActionSummary({ actions }: { actions: FlowActionResult[] }) {
  if (!actions.length) return <>未执行动作</>;
  if (actions.length > 1) return <>执行了 {actions.length} 个动作</>;

  const action = actions[0];
  const failed = action.status === "failed";
  if (action.type === "add_tags") {
    const count = getFlowActionResultTarget(action).length;
    return <>{failed ? "添加标签失败" : `添加了 ${count || 1} 个标签`}</>;
  }
  if (action.type === "move_to_folder") {
    const target = getFlowActionResultTarget(action)[0];
    return <>{failed ? "移动到文件夹失败" : target ? `移动到 ${target}` : "已移动到文件夹"}</>;
  }

  return <>{failed ? "动作执行失败" : "动作执行成功"}</>;
}

interface FlowRunsListProps {
  detailMode?: "inline" | "sheet";
  runs: FlowRun[];
  emptyDescription: string;
  hasNextPage?: boolean;
  isError?: boolean;
  isFetchingNextPage?: boolean;
  isPending?: boolean;
  onLoadMore?: () => void;
  onRuleClick?: (flowRuleId: string) => void;
  onRetry?: () => void;
  itemTitle?: string;
  showItem?: boolean;
  showRule?: boolean;
}

export function FlowRunsList({
  detailMode = "sheet",
  runs,
  emptyDescription,
  hasNextPage,
  isError,
  isFetchingNextPage,
  isPending,
  itemTitle,
  onLoadMore,
  onRuleClick,
  onRetry,
  showItem = false,
  showRule = false,
}: FlowRunsListProps) {
  const { open: openSheet } = useSheet();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  if (isPending) {
    return <div className="flex min-h-56 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  }

  if (isError) {
    return (
      <Empty className="min-h-56 border">
        <EmptyHeader>
          <EmptyMedia variant="icon"><AlertCircle /></EmptyMedia>
          <EmptyTitle>执行记录暂时无法加载</EmptyTitle>
          <EmptyDescription>请检查网络后重新加载。</EmptyDescription>
        </EmptyHeader>
        {onRetry ? <Button onClick={onRetry} variant="outline">重新加载</Button> : null}
      </Empty>
    );
  }

  if (!runs.length) {
    return (
      <Empty className="min-h-56 border">
        <EmptyHeader>
          <EmptyMedia variant="icon"><CircleDashed /></EmptyMedia>
          <EmptyTitle>暂无流程记录</EmptyTitle>
          <EmptyDescription>{emptyDescription}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-2">
      {runs.map((run) => {
        const expanded = expandedIds.has(run.id);
        return (
          <article
            className={cn(
              "rounded-lg",
              detailMode === "inline" ? "bg-muted/30" : "bg-background",
            )}
            key={run.id}
          >
            <button
              className="flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-left"
              aria-expanded={detailMode === "inline" ? expanded : undefined}
              onClick={() => {
                if (detailMode === "sheet") {
                  void openSheet(
                    FlowRunDetailSheet,
                    { itemTitle, run },
                    { className: "sm:max-w-xl" },
                  );
                  return;
                }

                setExpandedIds((current) => {
                  const next = new Set(current);
                  if (next.has(run.id)) next.delete(run.id); else next.add(run.id);
                  return next;
                });
              }}
              type="button"
            >
              <StatusIcon status={run.status} />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {showItem ? <ItemTitle itemId={run.item_id} /> : run.rule_version.name}
                  </span>
                  {run.stopped_following_rules ? <span className="shrink-0 text-xs text-muted-foreground">已停止后续流程</span> : null}
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  <ActionSummary actions={run.action_results} />
                </p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground/80" title={formatFullDateTime(run.created_at)}>
                {formatRelativeTime(run.created_at) || "未知时间"}
              </span>
              {detailMode === "inline" && expanded ? (
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              )}
            </button>
            {detailMode === "inline" && expanded ? (
              <div className="px-3 pb-3 pt-1">
                <FlowRunPath run={run} showTitle={false} />
              </div>
            ) : null}
            {showRule && onRuleClick && (detailMode !== "inline" || expanded) ? (
              <div className="px-3 pb-3 text-right">
                <Button
                  className={cn(
                    detailMode === "inline" && "h-auto gap-1 px-0 py-1 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground",
                  )}
                  onClick={() => onRuleClick(run.flow_rule_id)}
                  size="sm"
                  variant="ghost"
                >
                  查看流程
                  {detailMode === "inline" ? <ChevronRight className="size-3" /> : null}
                </Button>
              </div>
            ) : null}
          </article>
        );
      })}

      {hasNextPage ? (
        <div className="flex justify-center pt-2">
          <Button disabled={isFetchingNextPage} onClick={onLoadMore} variant="outline">
            {isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : null}
            加载更多
          </Button>
        </div>
      ) : null}
    </div>
  );
}
